import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowUpRight, FileText, ClipboardList, Table2, BookOpen, FileInput, Wrench, Share2 } from "lucide-react";

export const DESK_KEYS = ["docs", "social", "plans", "sheets", "knowledge", "forms", "tools"];

const CARDS = [
  { key: "docs", label: "Docs", icon: FileText, desc: "Write, review and send content and strategy docs.", color: "#723FFF", stat: (s) => `${s.docs} docs · ${s.docs_in_review} in review` },
  { key: "social", label: "SocialStudio", icon: Share2, desc: "Draft social posts, preview them per channel and get client approval.", color: "#E1306C", stat: (s) => `${s.social || 0} post groups · ${s.social_waiting || 0} in review` },
  { key: "plans", label: "Plans", icon: ClipboardList, desc: "Client and brand strategy plans with linked docs and sheets.", color: "#1f9d55", stat: (s) => `${s.plans} plans` },
  { key: "sheets", label: "Sheets", icon: Table2, desc: "Content calendars, trackers and imported spreadsheets.", color: "#0E7C86", stat: (s) => `${s.sheets} sheets` },
  { key: "knowledge", label: "Knowledge Base", icon: BookOpen, desc: "Guides, processes and answers for the team and clients.", color: "#D97706", stat: (s) => `${s.knowledge} articles` },
  { key: "forms", label: "Forms", icon: FileInput, desc: "Build briefs and live forms, and collect submissions.", color: "#DB2777", stat: (s) => `${s.forms} forms` },
  { key: "tools", label: "Tools", icon: Wrench, desc: "Keyword tracking and other agency utilities.", color: "#2563EB", stat: () => "Keyword tracking & more" },
];

export function BackToDesk({ onBack }) {
  return (
    <div className="px-6 md:px-10 pt-6 max-w-7xl mx-auto" data-testid="back-to-desk-bar">
      <button data-testid="back-to-desk" onClick={onBack} className="group inline-flex items-center gap-1.5 rounded-full border border-[#EEEFEF] bg-white px-3.5 py-1.5 text-sm text-[#595D62] hover:text-[#06060C] hover:border-[#d9ccff] transition-colors">
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Back to Desk
      </button>
    </div>
  );
}

export default function ConsoleDesk({ canOpen, onOpen }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/console/desk/summary").then(({ data }) => setS(data)).catch(() => setS({})); }, []);
  const cards = CARDS.filter((c) => canOpen(c.key));
  return (
    <main className="px-6 md:px-12 py-12 max-w-6xl mx-auto" data-testid="console-desk">
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Desk</h1>
      <p className="mt-2 text-sm text-[#9FA1A4] max-w-xl">Everything you write, plan and build, in one place. Pick a module to open it.</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c, i) => (
          <button key={c.key} data-testid={`desk-card-${c.key}`} onClick={() => onOpen(c.key)} style={{ animationDelay: `${i * 50}ms` }}
            className="group text-left rounded-3xl border border-[#EEEFEF] bg-white p-6 premium-shadow hover:-translate-y-0.5 hover:border-[#d9ccff] transition-[transform,border-color] duration-200 animate-fade-up">
            <div className="flex items-start justify-between">
              <span className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${c.color}14`, color: c.color }}><c.icon className="h-6 w-6" /></span>
              <ArrowUpRight className="h-5 w-5 text-[#c4c5cc] group-hover:text-[#06060C] transition-colors" />
            </div>
            <h2 className="font-display text-xl font-semibold text-[#06060C] mt-5">{c.label}</h2>
            <p className="text-sm text-[#9FA1A4] mt-1 min-h-[40px]">{c.desc}</p>
            <p className="mt-4 font-mono-data text-[11px] text-[#595D62]" data-testid={`desk-stat-${c.key}`}>{s ? (s[c.key] === undefined && c.key !== "tools" ? "—" : c.stat({ docs: 0, docs_in_review: 0, plans: 0, sheets: 0, knowledge: 0, forms: 0, ...s })) : "…"}</p>
          </button>
        ))}
      </div>
    </main>
  );
}
